import { type GetApiAlbumsQueryParams } from '@/shared/api'
import { combine, createStore, sample } from 'effector'
import { createGate } from 'effector-react'

import { albumCreate, albumDelete, albumUpdate } from '../api'
import {
  type AlbumsQueryModelWithPagination,
  type AlbumsSortState,
  createAlbumsQueryModel,
} from '../lib/albums-query-model'

interface AlbumsTableGateProps {
  initialFilters?: GetApiAlbumsQueryParams
  initialPage?: number
  initialPageSize?: number
  initialSearch?: string
  initialSort?: AlbumsSortState
}
export const AlbumsTableGate = createGate<AlbumsTableGateProps>()

export const AlbumsTableAlbumsQueryModel = createAlbumsQueryModel({
  usePagination: true,
  accumulate: false,
}) as AlbumsQueryModelWithPagination
const { availableSet, unavailableSet } = AlbumsTableAlbumsQueryModel
export const {
  //   Query
  query: albumsTableQuery,
  //   Init
  $filters: $albumsTableFilters,
  filtersInit: albumsTableFiltersInit,
  filtersChanged: albumsTableFiltersChanged,
  filtersReset: albumsTableFiltersReset,
  //   Search
  $search: $albumsTableSearch,
  searchChanged: albumsTableSearchChanged,
  searchReset: albumsTableSearchReset,
  //   Sort
  $sort: $albumsTableSort,
  sortChanged: albumsTableSortChanged,
  sortReset: albumsTableSortReset,
  //   Pagination
  $page: $albumsTablePage,
  pageChanged: albumsTablePageChanged,
  pageReset: albumsTablePageReset,
  $pageSize: $albumsTablePageSize,
  pageSizeChanged: albumsTablePageSizeChanged,
  //   Refresh
  refreshed: albumsTableRefreshed,
} = AlbumsTableAlbumsQueryModel

sample({
  clock: AlbumsTableGate.open,
  target: availableSet,
})

// Search input

export const $albumsTableSearchInputValue = createStore('')
  .on(albumsTableSearchChanged, (_, q) => q)
  .reset(albumsTableSearchReset)

// Setup initial params
// Page
sample({
  clock: AlbumsTableGate.open,
  source: combine(
    AlbumsTableGate.state,
    ({ initialPage }) => initialPage || null,
  ),
  filter: (page) => page !== null,
  target: albumsTablePageChanged,
})
// Page Size
sample({
  clock: AlbumsTableGate.open,
  source: combine(
    AlbumsTableGate.state,
    ({ initialPageSize }) => initialPageSize || null,
  ),
  filter: (pageSize) => pageSize !== null,
  target: albumsTablePageSizeChanged,
})
// Search
sample({
  clock: AlbumsTableGate.open,
  source: combine(
    AlbumsTableGate.state,
    ({ initialSearch }) => initialSearch || null,
  ),
  filter: (search) => search !== null,
  target: albumsTableSearchChanged,
})
// Sort
sample({
  clock: AlbumsTableGate.open,
  source: combine(
    AlbumsTableGate.state,
    ({ initialSort }) => initialSort || null,
  ),
  filter: (sort) => sort !== null,
  target: albumsTableSortChanged,
})

sample({
  source: AlbumsTableGate.state,
  clock: availableSet,
  fn: ({ initialFilters: params }) => params || {},
  target: albumsTableFiltersInit,
})

sample({
  clock: AlbumsTableGate.close,
  target: unavailableSet,
})

// Decrease page if there is only one record on page and it's being deleted
sample({
  source: combine({
    page: $albumsTablePage,
    queryData: albumsTableQuery.$data,
  }),
  clock: albumDelete.finished.success,
  filter: ({ queryData, page }) =>
    !queryData?.items.length || (queryData?.items.length <= 1 && page > 1),
  fn: ({ page }) => page - 1,
  target: albumsTablePageChanged,
})

sample({
  clock: [
    albumCreate.finished.success,
    albumUpdate.finished.success,
    albumDelete.finished.success,
  ],
  target: albumsTableRefreshed,
})
